import { ReactNode } from 'react';
import TopNavigationBarStores from 'components/TopNavigationBarStores';
import { useReportsLayoutTopBar } from '@reports/presentation/hooks/useReportsLayoutTopBar';

/**
 * Props for PageLayout component
 */
interface PageLayoutProps {
  /** Content to be rendered within the layout */
  children: ReactNode;
}

/**
 * Page Layout Component for Reports Domain
 *
 * Provides consistent layout structure for all report pages including:
 * - TopNavigationBarStores (blue bar) for store/organization context
 * - BannerErrorPublishMenus (integrated within TopNavigationBarStores) for menu publish errors
 * - Responsive layout with proper spacing for sidebar and mobile navbar
 *
 * Uses centralized layout hook (useReportsLayoutTopBar) for all dimension calculations.
 *
 * Spacing breakdown:
 * - Desktop: TopNav (56px) + optional Banner (56px) = 56-112px from top
 * - Mobile: Black navbar (56px) + TopNav (56px) + optional Banner (56px) = 112-168px from top
 * - Sidebar: 260px (expanded) or 64px (collapsed) on desktop
 *
 * @param {PageLayoutProps} props - Component properties
 * @returns {React.ReactElement} Page layout wrapper
 *
 * @example
 * <PageLayout>
 *   <SalesReportContent />
 * </PageLayout>
 */
export const PageLayout = ({ children }: PageLayoutProps): React.ReactElement => {
  const { topNavWidthClasses, workspaceWidthClasses, topMarginClasses, heightClasses } = useReportsLayoutTopBar();

  return (
    <div className="flex flex-col w-full">
      {/*
        Top Navigation Bar - Blue bar with store/organization context
        - Desktop: Fixed at top, width accounts for sidebar
        - Mobile: Fixed below black navbar (top-[56px])

        Note: BannerErrorPublishMenus is rendered inside TopNavigationBarStores
        when enableValidateItemsAvailability && isPublishMenuError && !modeDraft
      */}
      <div className={`fixed z-1250 w-full top-[56px] sm:top-0 ${topNavWidthClasses}`}>
        <TopNavigationBarStores />
      </div>

      {/*
        Main Content Workspace
        - Margin-top: Accounts for TopNav + optional Banner + mobile navbar
        - Height: Viewport minus top elements
        - Width: Full width on mobile, accounts for sidebar on desktop
      */}
      <div
        className={`flex flex-col overflow-auto w-full ${topMarginClasses} ${heightClasses} ${workspaceWidthClasses}`}
      >
        {children}
      </div>
    </div>
  );
};
